import { useAuth } from '@/auth/Auth';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import LayoutOutside from '@/layout/LayoutOutside';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setPostLoginRedirect } from '@/utils/postLoginRedirect';
import { useFetchPartnerInvite } from './hooks/useFetchPartnerInvite';
import { useApplyPartnerInvitation } from './hooks/useApplyPartnerInvitation';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEffect } from 'react';
import Loader from '@/components/Loader/Loader';

export function InvitationPartnerLink() {
  const { productId } = useParams<{ productId: string }>();
  const { product, isPending } = useFetchPartnerInvite(productId ?? '');
  const { applyPartnerInvitation, isConfiguring } = useApplyPartnerInvitation();

  const { t } = useTranslation('invitationPartnerLink');
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const handlePartnerClick = async () => {
    if (!productId) return;

    if (isAuthenticated) {
      applyPartnerInvitation(productId);
    } else {
      const redirectPath = `/invitation-partner/${productId}`;
      setPostLoginRedirect(redirectPath);
      navigate('/login', {
        state: { from: redirectPath },
      });
    }
  };

  useEffect(() => {
    const redirectUrl = localStorage.getItem('redirectAfterEmailVerification');

    const handleBeforeUnload = () => {
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterEmailVerification');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      const authPaths = ['/login', '/register', '/verify-email', '/verify-password'];
      const isAuthNavigation = authPaths.some((path) =>
        location.pathname.startsWith(path),
      );

      if (
        redirectUrl &&
        !isAuthNavigation &&
        location.pathname !== `/invitation-partner/${productId}`
      ) {
        localStorage.removeItem('redirectAfterEmailVerification');
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname, productId]);

  const commissionText = (() => {
    if (!product) return '';
    if (product.commission_type === 'percentage') {
      return product.commission_value_formatted ?? `${product.commission_value / 100}%`;
    }
    return product.commission_value_formatted ?? String(product.commission_value ?? '');
  })();

  const durationText = product?.is_lifetime
    ? t('lifetime')
    : `${product?.duration_days ?? 0} ${t('days')}`;

  return (
    <LayoutOutside classNameMainContainer="mt-[10%]" hideButton>
      {isPending ? (
        <Loader fullScreen />
      ) : (
        <>
          {product ? (
            <section className="max-w-md w-full flex flex-col items-center space-y-8">
              <Avatar className="h-20 w-20 bg-blackA1">
                <AvatarImage
                  src={product.product.images[0]?.url}
                  className="w-full h-full rounded-full"
                  alt={`${product.product.name} image`}
                  loading="lazy"
                />
                <AvatarFallback className="bg-elevated text-xl font-medium text-muted">
                  {product.product.name[0]}
                </AvatarFallback>
              </Avatar>

              <div className="space-y-1 text-center">
                <h4 className="font-medium">{t('invite_partnership')}</h4>
                <h1 className="text-3xl font-semibold">
                  {product.product.name}
                </h1>
              </div>

              <div className="border w-full p-4 space-y-6 rounded">
                <Table className="w-full">
                  <TableBody>
                    <TableRow className="border-transparent">
                      <TableCell className="text-gray-400">
                        {t('producer')}:
                      </TableCell>
                      <TableCell className="text-white">
                        {product.product.producer_name}
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-transparent">
                      <TableCell className="text-gray-400">
                        {t('commission')}:
                      </TableCell>
                      <TableCell className="text-white">
                        {commissionText}
                      </TableCell>
                    </TableRow>
                    <TableRow className="border-transparent">
                      <TableCell className="text-gray-400">
                        {t('duration')}:
                      </TableCell>
                      <TableCell className="text-white">
                        {durationText}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Button
                  className="w-full"
                  onClick={handlePartnerClick}
                  disabled={isConfiguring}
                >
                  {t('accept_now')}
                </Button>
              </div>
            </section>
          ) : (
            <div>
              <h2>{t('no_invite')}</h2>
            </div>
          )}
        </>
      )}
    </LayoutOutside>
  );
}
